import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Fuel, Gauge, Settings2, Zap, Scale, Check } from "lucide-react";
import { FORMAT, FUEL_BG, TRANS_BG } from "@/lib/site";
import { Link } from "react-router-dom";
import { useCompare } from "@/lib/compare";

const FALLBACK =
    "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?auto=format&fit=crop&w=1600&q=80";

export default function CarCard({ car, index = 0 }) {
    const { toggle, isCompared } = useCompare();
    const compared = isCompared(car.id);

    // First 3 photos cycle on hover
    const imgs = (car.images && car.images.length ? car.images : [FALLBACK]).slice(0, 3);
    const [active, setActive] = useState(0);
    const [hovering, setHovering] = useState(false);

    useEffect(() => {
        if (!hovering || imgs.length < 2) return;
        const id = setInterval(() => {
            setActive((i) => (i + 1) % imgs.length);
        }, 1100);
        return () => clearInterval(id);
    }, [hovering, imgs.length]);

    useEffect(() => {
        if (!hovering) setActive(0);
    }, [hovering]);

    return (
        <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6 }}
            className="group relative bg-surface rounded-2xl overflow-hidden border border-line gold-border-anim transition-shadow duration-500 hover:shadow-soft"
            data-testid={`car-card-${car.id}`}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
        >
            {/* Compare toggle — top-left */}
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggle(car.id);
                }}
                className={`absolute top-4 left-4 z-20 w-10 h-10 rounded-full backdrop-blur-md border flex items-center justify-center transition-all ${
                    compared
                        ? "bg-gold border-gold text-champagne"
                        : "bg-bg/70 border-line text-ink/80 hover:border-gold hover:text-gold"
                }`}
                title={compared ? "Премахни от сравнението" : "Добави към сравнение"}
                data-testid={`car-compare-toggle-${car.id}`}
                aria-label="Сравни"
            >
                {compared ? <Check size={16} /> : <Scale size={16} />}
            </button>

            <Link to={`/cars/${car.id}`} className="block">
                {/* Image carousel */}
                <div className="relative aspect-[16/10] overflow-hidden bg-ink/5" data-testid={`car-carousel-${car.id}`}>
                    <AnimatePresence mode="popLayout">
                        <motion.img
                            key={imgs[active]}
                            src={imgs[active]}
                            alt={`${car.brand} ${car.model} — снимка ${active + 1}`}
                            loading="lazy"
                            initial={{ opacity: 0, scale: 1.06 }}
                            animate={{ opacity: 1, scale: hovering ? 1.06 : 1 }}
                            exit={{ opacity: 0, scale: 1 }}
                            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    </AnimatePresence>
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent pointer-events-none" />

                    {car.sold && (
                        <span className="absolute top-4 left-1/2 -translate-x-1/2 bg-red-700/85 text-white text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full">
                            Продаден
                        </span>
                    )}
                    <span className="absolute top-4 right-4 bg-bg/75 backdrop-blur-md border border-line text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full text-ink-muted">
                        {car.brand}
                    </span>

                    {/* Dots indicator (only if multiple images) */}
                    {imgs.length > 1 && (
                        <div
                            className={`absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 transition-opacity duration-300 ${
                                hovering ? "opacity-100" : "opacity-0"
                            }`}
                            data-testid={`car-dots-${car.id}`}
                        >
                            {imgs.map((_, i) => (
                                <span
                                    key={i}
                                    className={`h-1.5 rounded-full transition-all ${
                                        i === active ? "w-6 bg-gold" : "w-1.5 bg-white/60"
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="p-7">
                    <div className="flex items-start justify-between gap-4 mb-5">
                        <div>
                            <h3
                                className="font-display text-2xl leading-tight text-ink"
                                data-testid={`car-title-${car.id}`}
                            >
                                {car.brand} {car.model}
                            </h3>
                            <p className="text-ink-muted text-sm mt-1">
                                {car.color || car.body_style}
                            </p>
                        </div>
                        <div className="text-right shrink-0">
                            <div
                                className="text-gold font-display text-2xl"
                                data-testid={`car-price-${car.id}`}
                            >
                                {FORMAT.money(car.price)}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm text-ink-muted mb-6">
                        <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-gold" />
                            {car.year}
                        </div>
                        <div className="flex items-center gap-2">
                            <Gauge size={14} className="text-gold" />
                            {FORMAT.km(car.mileage)}
                        </div>
                        <div className="flex items-center gap-2">
                            <Fuel size={14} className="text-gold" />
                            {FUEL_BG[car.fuel] || car.fuel}
                        </div>
                        <div className="flex items-center gap-2">
                            <Settings2 size={14} className="text-gold" />
                            {TRANS_BG[car.transmission] || car.transmission}
                        </div>
                        <div className="flex items-center gap-2 col-span-2">
                            <Zap size={14} className="text-gold" />
                            {FORMAT.hp(car.horsepower)}
                        </div>
                    </div>

                    <div className="pt-5 border-t border-line flex items-center justify-between">
                        <span className="text-xs tracking-[0.25em] uppercase text-ink-muted">
                            Виж детайли
                        </span>
                        <span className="w-9 h-9 rounded-full border border-gold text-gold flex items-center justify-center transition-all duration-300 group-hover:bg-gold group-hover:text-champagne">
                            →
                        </span>
                    </div>
                </div>
            </Link>
        </motion.article>
    );
}
